import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { MailCheck, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n/I18nContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Auth() {
  const [mode, setMode] = useState("login");
  const [step, setStep] = useState("form"); // form | code
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, requestRegisterCode, verifyRegisterCode, requestResetCode, verifyReset } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();

  const goHome = (user) => navigate(user.role === "admin" ? "/admin" : "/");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        const user = await login(form.email, form.password);
        toast.success(t("auth.signIn"));
        goHome(user);
      } else if (mode === "reset") {
        await requestResetCode(form.email);
        setStep("code");
        toast.success(t("auth.codeSent"));
      } else {
        const res = await requestRegisterCode(form.name, form.email, form.password);
        if (res.user) {
          toast.success(t("auth.createAccount"));
          goHome(res.user);
        } else {
          setStep("code");
          toast.success(t("auth.codeSent"));
        }
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("auth.authError"));
    } finally {
      setLoading(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "reset") {
        const user = await verifyReset(form.email, code, form.password);
        toast.success(t("auth.resetDone"));
        goHome(user);
      } else {
        const user = await verifyRegisterCode(form.email, code);
        toast.success(t("auth.createAccount"));
        goHome(user);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("auth.wrongCode"));
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    setLoading(true);
    try {
      if (mode === "reset") await requestResetCode(form.email);
      else await requestRegisterCode(form.name, form.email, form.password);
      toast.success(t("auth.codeSent"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("auth.authError"));
    } finally {
      setLoading(false);
    }
  };

  const switchMode = () => {
    setMode(mode === "login" ? "register" : "login");
    setStep("form");
    setCode("");
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-gradient-to-b from-accent/40 to-background">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md bg-card rounded-3xl border border-border p-8 shadow-[0_20px_40px_rgb(4,120,87,0.08)]">
        <div className="flex justify-center mb-4"><LanguageSwitcher /></div>
        <Link to="/" className="flex items-center justify-center gap-2.5 mb-6">
          <img src="/logo-mark.png" alt="NutriScan" className="h-12 w-12 rounded-2xl" />
          <span className="font-heading font-extrabold text-2xl text-slate-900 tracking-tight">NutriScan</span>
        </Link>

        {step === "code" ? (
          <>
            <div className="flex flex-col items-center text-center mb-6">
              <div className="w-14 h-14 rounded-2xl bg-emerald-100 flex items-center justify-center mb-3">
                <MailCheck className="w-7 h-7 text-emerald-600" />
              </div>
              <h1 className="font-heading text-2xl font-extrabold">{mode === "reset" ? t("auth.resetTitle") : t("auth.codeTitle")}</h1>
              <p className="text-sm text-muted-foreground mt-1">{t("auth.codeSub")} <b>{form.email}</b></p>
            </div>
            <form onSubmit={verify} className="space-y-4">
              <div>
                <Label htmlFor="code">{t("auth.codeLabel")}</Label>
                <Input id="code" data-testid="auth-code-input" required inputMode="numeric" maxLength={6}
                  value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  className="mt-1.5 h-14 rounded-xl text-center text-2xl font-bold tracking-[0.5em]" placeholder="______" />
              </div>
              {mode === "reset" && (
                <div>
                  <Label htmlFor="newpass">{t("auth.newPassword")}</Label>
                  <Input id="newpass" type="password" data-testid="auth-newpassword-input" required minLength={6}
                    value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                    className="mt-1.5 h-12 rounded-xl" placeholder={t("auth.passwordPh")} />
                </div>
              )}
              <Button type="submit" data-testid="auth-verify-button" disabled={loading || code.length < 6 || (mode === "reset" && form.password.length < 6)}
                className="w-full h-12 rounded-xl bg-primary hover:bg-primary/90 font-semibold">
                {loading ? t("common.loading") : mode === "reset" ? t("auth.resetDoneBtn") : t("auth.verify")}
              </Button>
            </form>
            <div className="flex items-center justify-between mt-5 text-sm">
              <button onClick={() => setStep("form")} data-testid="auth-back-button"
                className="flex items-center gap-1 text-muted-foreground hover:text-foreground">
                <ArrowLeft className="w-4 h-4" /> {t("common.back")}
              </button>
              <button onClick={resend} data-testid="auth-resend-button" disabled={loading}
                className="text-primary font-semibold hover:underline">{t("auth.resend")}</button>
            </div>
          </>
        ) : (
          <>
            <h1 className="font-heading text-2xl font-extrabold text-center">
              {mode === "login" ? t("auth.welcome") : mode === "reset" ? t("auth.resetTitle") : t("auth.createAccount")}
            </h1>
            <p className="text-sm text-muted-foreground text-center mt-1 mb-6">
              {mode === "login" ? t("auth.loginSub") : mode === "reset" ? t("auth.resetSub") : t("auth.registerSub")}
            </p>

            <form onSubmit={submit} className="space-y-4">
              {mode === "register" && (
                <div>
                  <Label htmlFor="name">{t("auth.name")}</Label>
                  <Input id="name" data-testid="auth-name-input" required value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="mt-1.5 h-12 rounded-xl" placeholder={t("auth.namePh")} />
                </div>
              )}
              <div>
                <Label htmlFor="email">{t("auth.email")}</Label>
                <Input id="email" type="email" data-testid="auth-email-input" required value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="mt-1.5 h-12 rounded-xl" placeholder="you@example.com" />
              </div>
              {mode !== "reset" && (
                <div>
                  <Label htmlFor="password">{t("auth.password")}</Label>
                  <Input id="password" type="password" data-testid="auth-password-input" required minLength={6} value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    className="mt-1.5 h-12 rounded-xl" placeholder={t("auth.passwordPh")} />
                </div>
              )}
              {mode === "login" && (
                <div className="text-right -mt-1">
                  <button type="button" data-testid="auth-forgot-button"
                    onClick={() => { setMode("reset"); setStep("form"); setCode(""); }}
                    className="text-sm text-primary font-semibold hover:underline">{t("auth.forgot")}</button>
                </div>
              )}
              {mode === "register" && (
                <p className="text-xs text-muted-foreground">{t("auth.codeHint")}</p>
              )}
              <Button type="submit" data-testid="auth-submit-button" disabled={loading}
                className="w-full h-12 rounded-xl bg-primary hover:bg-primary/90 font-semibold">
                {loading ? t("common.loading") : mode === "login" ? t("auth.signIn") : mode === "reset" ? t("auth.sendCode") : t("auth.signUp")}
              </Button>
            </form>
            {mode === "reset" ? (
              <p className="text-center text-sm text-muted-foreground mt-6">
                <button data-testid="auth-back-login" onClick={() => { setMode("login"); setStep("form"); }}
                  className="text-primary font-semibold hover:underline">{t("common.back")}</button>
              </p>
            ) : (
            <p className="text-center text-sm text-muted-foreground mt-6">
              {mode === "login" ? t("auth.noAccount") : t("auth.haveAccount")}{" "}
              <button data-testid="auth-toggle-button" onClick={switchMode}
                className="text-primary font-semibold hover:underline">
                {mode === "login" ? t("auth.signUp") : t("auth.signIn")}
              </button>
            </p>
            )}
          </>
        )}
      </motion.div>
    </div>
  );
}
